import React, { useEffect, useMemo } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BracketBoard } from '@/components/bracket/BracketBoard';
import { buildBracketModel } from '@/lib/bracket/model';
import { bracketFormatsFor } from '@/lib/bracket/templates';
import { SeedingList } from './SeedingList';
import { MatchupEditor } from './MatchupEditor';
import { useWizardBracket } from './useWizardBracket';

const Stat = ({ label, value, testId }) => (
  <div className="rounded-lg bg-white border border-slate-200 px-3 py-2">
    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{label}</p>
    <p className="text-xl font-black text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }} data-testid={testId}>{value}</p>
  </div>
);

const Summary = ({ data, teams }) => {
  const firstRound = Math.min(...data.schedule.map((m) => m.round));
  const byes = [];
  for (let i = 0; i < data.positions.length; i += 2) {
    const [a, b] = [data.positions[i], data.positions[i + 1]];
    if (a === 'BYE' || b === 'BYE') byes.push(a === 'BYE' ? b : a);
  }
  const name = (id) => { const i = teams.findIndex((t) => t.id === id); return `${i + 1}. ${teams[i]?.name}`; };
  return (
    <div className="space-y-2" data-testid="bracket-summary">
      <div className="grid grid-cols-3 gap-2">
        <Stat label="Total games" value={data.total_matches} testId="bracket-summary-games" />
        <Stat label="First-round games" value={data.schedule.filter((m) => m.round === firstRound).length} testId="bracket-summary-first-round" />
        <Stat label="Byes" value={byes.length} testId="bracket-summary-byes" />
      </div>
      {byes.length > 0 && <p className="text-xs text-slate-500" data-testid="bracket-summary-bye-teams">Start in round 2: {byes.map(name).join(', ')}</p>}
    </div>
  );
};

export const BracketTournamentPanel = ({ eventData, setEventData }) => {
  const { teams, format } = eventData;
  const options = bracketFormatsFor(teams.length);
  const current = options.find((o) => o.format === format);
  const ids = teams.map((t) => t.id).sort().join();

  useEffect(() => {
    if (!current?.ok) {
      const first = options.find((o) => o.ok);
      if (first) setEventData((p) => ({ ...p, format: first.format, bracket_positions: null }));
    }
  }, [current?.ok]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const pos = eventData.bracket_positions;
    if (pos && pos.filter((p) => p !== 'BYE').sort().join() !== ids) setEventData((p) => ({ ...p, bracket_positions: null }));
  }, [ids]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data, error } = useWizardBracket(format, teams, eventData.bracket_positions);
  const model = useMemo(() => (data ? buildBracketModel({ format: data.format, schedule: data.schedule }) : null), [data]);
  const setPositions = (bracket_positions) => setEventData((p) => ({ ...p, bracket_positions }));

  return (
    <div className="-mt-2 rounded-xl border-2 border-[#00247D]/20 bg-[#00247D]/[0.03] p-4 space-y-4 animate-in fade-in duration-300" data-testid="bracket-tournament-panel">
      <div className="space-y-1.5">
        <Label htmlFor="bracket-format-select" className="font-bold text-slate-800">Select Bracket Format</Label>
        <Select value={format} onValueChange={(f) => setEventData((p) => ({ ...p, format: f, bracket_positions: null }))}>
          <SelectTrigger id="bracket-format-select" className="bg-white h-auto min-h-9 text-left whitespace-normal [&>span]:line-clamp-2" data-testid="bracket-format-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <SelectItem key={o.format} value={o.format} disabled={!o.ok} data-testid={`bracket-format-option-${o.format}`}>
                {o.label}{o.ok ? ` · ${o.games} games${o.byes ? ` · ${o.byes} byes` : ''}` : ` (${o.reason})`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-slate-500">Formats from the Bracket Library that fit your {teams.length} teams. Every game is created for you and winners advance as scores come in.</p>
      </div>
      {error && <p className="flex items-center gap-2 text-sm text-[#CF142B]" data-testid="bracket-preview-error"><AlertTriangle className="w-4 h-4" />{error}</p>}
      {!data ? <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-[#00247D]" /></div> : (
        <>
          <Summary data={data} teams={teams} />
          <Tabs defaultValue="preview">
            <TabsList className="bg-white border border-slate-200">
              <TabsTrigger value="preview" data-testid="bracket-tab-preview">Preview</TabsTrigger>
              <TabsTrigger value="seeds" data-testid="bracket-tab-seeds">Seeds</TabsTrigger>
              <TabsTrigger value="matchups" data-testid="bracket-tab-matchups">Matchups{eventData.bracket_positions ? ' · custom' : ''}</TabsTrigger>
            </TabsList>
            <TabsContent value="preview" className="mt-3" data-testid="bracket-wizard-preview">
              {model && <BracketBoard compact model={model} title={(eventData.name || 'Bracket').toUpperCase()} subtitle={current?.label} logos={{}} storageKey="bracket-layout:wizard" />}
            </TabsContent>
            <TabsContent value="seeds" className="mt-3"><SeedingList teams={teams} setEventData={setEventData} /></TabsContent>
            <TabsContent value="matchups" className="mt-3">
              <MatchupEditor positions={data.positions} teams={teams} custom={!!eventData.bracket_positions} onChange={setPositions} onReset={() => setPositions(null)} />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};
